import React, { useState, useEffect, useCallback, useRef } from 'react';
import { API_BASE } from './config/api';

const API = API_BASE;   // direccion del backend: config/api.js (fuente unica)
const STORAGE_KEY = 'expo_mapa_estado';
const PP = "'Poppins', sans-serif";

const TIPOS = [
  { id: 'nodos', label: 'Nodos de red', emoji: '🔵', color: '#3b82f6', porZona: 3 },
  { id: 'cctv', label: 'CCTV', emoji: '🔴', color: '#ef4444', porZona: 2 },
  { id: 'ap', label: 'Access Points', emoji: '🟢', color: '#22c55e', porZona: 2 },
  { id: 'rack', label: 'Racks', emoji: '🟠', color: '#f97316', porZona: 1 },
  { id: 'arcos', label: 'Arcos', emoji: '🟣', color: '#a855f7', porZona: 1 },
  { id: 'cableado', label: 'Cableado', emoji: '⚫', color: '#6b7280', porZona: 3 },
];

const ZONAS = [
  { id: 1, nombre: 'Registro de Clientes', emoji: '📋' },
  { id: 2, nombre: 'Entrada VIP', emoji: '⭐' },
  { id: 3, nombre: 'Zona de Stands', emoji: '🏢' },
  { id: 4, nombre: 'Zona de Alimentos', emoji: '🍽️' },
  { id: 5, nombre: 'Cajas', emoji: '💳' },
  { id: 6, nombre: 'Centro de Negocios', emoji: '💼' },
  { id: 7, nombre: 'Zona VIP', emoji: '🌟' },
  { id: 8, nombre: 'Registro de Proveedores', emoji: '📦' },
  { id: 9, nombre: 'Bodegas', emoji: '🏭' },
  { id: 10, nombre: 'Seguridad', emoji: '🔒' },
];

const ESTADOS = {
  pendiente:  { color: '#fee2e2', texto: '#991b1b', label: 'Pendiente' },
  en_proceso: { color: '#fef9c3', texto: '#854d0e', label: 'En proceso' },
  completado: { color: '#dcfce7', texto: '#166534', label: 'Completado' },
  incidente:  { color: '#ff1744', texto: '#ffffff', label: 'Incidente', pulsa: true },
};

function crearDatosIniciales() {
  const datos = {};
  TIPOS.forEach(tipo => {
    datos[tipo.id] = {};
    ZONAS.forEach(zona => {
      const puntos = [];
      for (let i = 1; i <= tipo.porZona; i++) {
        puntos.push({
          id: `${tipo.id}-${zona.id}-${i}`,
          estado: 'pendiente',
          tecnico: '',
          notas: '',
          foto: '',
          hora: '',
          incidente_desc: '',
          incidente_urgencia: 'media',
        });
      }
      datos[tipo.id][zona.id] = { puntos };
    });
  });
  return datos;
}

function cargarEstado() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  const datos = crearDatosIniciales();
  localStorage.setItem(STORAGE_KEY, JSON.stringify(datos));
  return datos;
}

function guardarEstado(datos) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(datos));
}

// ============================================
// COMPONENTE PRINCIPAL
// ============================================
export default function MapaExpo({ usuario, onVolver }) {
  const [datos, setDatos] = useState(cargarEstado);
  const [tipoActivo, setTipoActivo] = useState('nodos');
  const [nivel, setNivel] = useState(1);
  const [zonaActiva, setZonaActiva] = useState(null);
  const [puntoActivo, setPuntoActivo] = useState(null);
  const [toast, setToast] = useState('');

  // Persistir cambios
  useEffect(() => {
    guardarEstado(datos);
  }, [datos]);

  // Toast
  const mostrarToast = useCallback((msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2500);
  }, []);

  // Calcular stats
  const statsGlobal = useCallback(() => {
    let total = 0, completados = 0;
    TIPOS.forEach(t => {
      ZONAS.forEach(z => {
        const puntos = datos[t.id]?.[z.id]?.puntos || [];
        total += puntos.length;
        completados += puntos.filter(p => p.estado === 'completado').length;
      });
    });
    return { total, completados, pct: total > 0 ? Math.round((completados / total) * 100) : 0 };
  }, [datos]);

  const statsTipo = useCallback((tipoId) => {
    let total = 0, completados = 0, incidentes = 0;
    ZONAS.forEach(z => {
      const puntos = datos[tipoId]?.[z.id]?.puntos || [];
      total += puntos.length;
      completados += puntos.filter(p => p.estado === 'completado').length;
      incidentes += puntos.filter(p => p.estado === 'incidente').length;
    });
    return { total, completados, incidentes, pct: total > 0 ? Math.round((completados / total) * 100) : 0 };
  }, [datos]);

  const statsZona = useCallback((tipoId, zonaId) => {
    const puntos = datos[tipoId]?.[zonaId]?.puntos || [];
    const total = puntos.length;
    const completados = puntos.filter(p => p.estado === 'completado').length;
    const incidentes = puntos.filter(p => p.estado === 'incidente').length;
    return { total, completados, incidentes, pct: total > 0 ? Math.round((completados / total) * 100) : 0 };
  }, [datos]);

  const actualizarPunto = useCallback((tipoId, zonaId, puntoId, cambios) => {
    setDatos(prev => {
      const nuevo = JSON.parse(JSON.stringify(prev));
      const puntos = nuevo[tipoId][zonaId].puntos;
      const idx = puntos.findIndex(p => p.id === puntoId);
      if (idx !== -1) {
        puntos[idx] = { ...puntos[idx], ...cambios };
      }
      return nuevo;
    });
  }, []);

  const global = statsGlobal();

  // ============================================
  // NIVEL 3: DETALLE DE PUNTO (Bottom Sheet)
  // ============================================
  if (puntoActivo) {
    return (
      <DetallesPunto
        punto={puntoActivo}
        tipo={TIPOS.find(t => t.id === tipoActivo)}
        zona={ZONAS.find(z => z.id === zonaActiva)}
        usuario={usuario}
        onGuardar={(cambios) => {
          actualizarPunto(tipoActivo, zonaActiva, puntoActivo.id, cambios);
          mostrarToast('Guardado correctamente');
          setPuntoActivo(null);
        }}
        onCerrar={() => setPuntoActivo(null)}
      />
    );
  }

  // ============================================
  // NIVEL 2: VISTA DE ZONA
  // ============================================
  if (nivel === 2 && zonaActiva) {
    const zona = ZONAS.find(z => z.id === zonaActiva);
    const tipo = TIPOS.find(t => t.id === tipoActivo);
    const puntos = datos[tipoActivo]?.[zonaActiva]?.puntos || [];

    return (
      <div style={{ minHeight: '100dvh', background: '#f3f4f4', fontFamily: PP }}>
        <style>{`
          @keyframes pulsoIncidente { 0%,100% { opacity:1; } 50% { opacity:0.4; } }
          .incidente-activo { animation: pulsoIncidente 0.6s ease-in-out infinite; }
        `}</style>

        {/* Header */}
        <div style={{
          background: '#fff', padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 12,
          borderBottom: '1px solid #e8e8e8', position: 'sticky', top: 0, zIndex: 10,
        }}>
          <button onClick={() => { setNivel(1); setZonaActiva(null); }} style={{
            background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', padding: 0, minWidth: 44, minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>←</button>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 15 }}>{zona.emoji} {zona.nombre}</div>
            <div style={{ fontSize: 12, color: tipo.color, fontWeight: 600 }}>{tipo.emoji} {tipo.label}</div>
          </div>
        </div>

        {/* Mapa SVG con puntos */}
        <div style={{ padding: '16px 12px', position: 'relative' }}>
          <div style={{
            position: 'relative', background: '#fff', borderRadius: 14, overflow: 'hidden',
            border: '1px solid #e8e8e8', boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          }}>
            <img
              src="/Plano Expo Alianza 2026 v3.svg"
              alt="Plano Expo"
              style={{ width: '100%', display: 'block', opacity: 0.35 }}
            />
            {/* Overlay de puntos en cuadricula */}
            <div style={{
              position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: '20%',
            }}>
              <div style={{
                display: 'flex', flexWrap: 'wrap', gap: 16, justifyContent: 'center', alignItems: 'center',
              }}>
                {puntos.map((punto, i) => {
                  const est = ESTADOS[punto.estado] || ESTADOS.pendiente;
                  return (
                    <div
                      key={punto.id}
                      className={punto.estado === 'incidente' ? 'incidente-activo' : ''}
                      onClick={() => setPuntoActivo(punto)}
                      style={{
                        width: 40, height: 40, borderRadius: '50%',
                        background: est.color, border: `3px solid ${est.texto}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 14, fontWeight: 700, color: est.texto,
                        cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                        transition: 'transform 0.15s',
                      }}
                    >
                      {i + 1}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Lista de puntos */}
        <div style={{ padding: '0 16px 100px' }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#333', marginBottom: 12 }}>
            Puntos de instalación
          </div>
          {puntos.map((punto, i) => {
            const est = ESTADOS[punto.estado] || ESTADOS.pendiente;
            const esIncidente = punto.estado === 'incidente';
            return (
              <div
                key={punto.id}
                className={esIncidente ? 'incidente-activo' : ''}
                onClick={() => setPuntoActivo(punto)}
                style={{
                  background: esIncidente ? '#fff0f0' : '#fff',
                  borderRadius: 12, padding: '14px 16px', marginBottom: 8,
                  border: esIncidente ? '2px solid #ff1744' : '1px solid #e8e8e8',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12,
                }}
              >
                <div style={{
                  width: 36, height: 36, borderRadius: '50%', background: est.color,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 14, fontWeight: 700, color: est.texto, flexShrink: 0,
                }}>
                  {i + 1}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                    Punto {i + 1}
                    {esIncidente && <span style={{ fontSize: 12 }}>⚠️ INCIDENTE</span>}
                  </div>
                  {punto.tecnico && (
                    <div style={{ fontSize: 12, color: '#5e666e', marginTop: 2 }}>{'👤'} {punto.tecnico}</div>
                  )}
                  {punto.hora && (
                    <div style={{ fontSize: 11, color: '#22c55e', marginTop: 1 }}>✓ {punto.hora}</div>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {punto.foto && <span style={{ fontSize: 16 }}>{'📷'}</span>}
                  <div style={{
                    fontSize: 10, fontWeight: 700, color: est.texto, background: est.color,
                    padding: '4px 10px', borderRadius: 20, whiteSpace: 'nowrap',
                  }}>
                    {est.label}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Toast */}
        {toast && <Toast mensaje={toast} />}
      </div>
    );
  }

  // ============================================
  // NIVEL 1: VISTA GENERAL
  // ============================================
  const tipoStats = statsTipo(tipoActivo);
  const tipoObj = TIPOS.find(t => t.id === tipoActivo);

  return (
    <div style={{ minHeight: '100dvh', background: '#f3f4f4', fontFamily: PP }}>
      <style>{`
        @keyframes pulsoBorde {
          0%,100% { border-color: #ff1744; box-shadow: 0 0 0 0 rgba(255,23,68,0.4); }
          50% { border-color: #ff6d00; box-shadow: 0 0 0 8px rgba(255,23,68,0); }
        }
        @keyframes pulsoIncidente { 0%,100% { opacity:1; } 50% { opacity:0.4; } }
        .incidente-activo { animation: pulsoIncidente 0.6s ease-in-out infinite; }
        .zona-incidente { animation: pulsoBorde 1.2s ease-in-out infinite; }
      `}</style>

      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #2f5f9e, #5c9adb)', padding: '16px 20px',
        color: '#fff', display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <button onClick={onVolver} style={{
          background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', borderRadius: 10,
          fontSize: 18, cursor: 'pointer', minWidth: 44, minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>←</button>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 17 }}>{'🗺️'} Mapa de Avances</div>
          <div style={{ fontSize: 12, opacity: 0.85 }}>
            {new Date().toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' })}
          </div>
        </div>
      </div>

      {/* Barra de progreso general */}
      <div style={{ padding: '16px 20px 8px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>Progreso total</span>
          <span style={{ fontSize: 20, fontWeight: 800, color: '#2f5f9e' }}>{global.pct}%</span>
        </div>
        <div style={{ background: '#e0e0e0', borderRadius: 8, height: 10, overflow: 'hidden' }}>
          <div style={{
            width: `${global.pct}%`, height: '100%', borderRadius: 8, transition: 'width 0.5s',
            background: global.pct >= 70 ? '#22c55e' : global.pct >= 30 ? '#f97316' : '#ef4444',
          }} />
        </div>
        <div style={{ fontSize: 12, color: '#5e666e', marginTop: 4 }}>
          {global.completados}/{global.total} completados
        </div>
      </div>

      {/* Tabs de tipos */}
      <div style={{
        display: 'flex', gap: 8, overflowX: 'auto', padding: '8px 20px 4px',
        WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none',
      }}>
        {TIPOS.map(tipo => {
          const st = statsTipo(tipo.id);
          const activo = tipoActivo === tipo.id;
          return (
            <button
              key={tipo.id}
              onClick={() => setTipoActivo(tipo.id)}
              style={{
                flexShrink: 0, padding: '10px 16px', borderRadius: 12,
                border: activo ? `2px solid ${tipo.color}` : '2px solid transparent',
                background: activo ? `${tipo.color}15` : '#fff',
                cursor: 'pointer', fontFamily: PP, fontSize: 13, fontWeight: 600,
                color: activo ? tipo.color : '#555', display: 'flex', alignItems: 'center', gap: 6,
                minHeight: 44, position: 'relative',
              }}
            >
              <span>{tipo.emoji}</span>
              <span>{tipo.label}</span>
              {st.incidentes > 0 && (
                <span style={{
                  position: 'absolute', top: -4, right: -4,
                  background: '#ff1744', color: '#fff', fontSize: 10, fontWeight: 700,
                  borderRadius: 10, minWidth: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  padding: '0 4px',
                }}>
                  {st.incidentes}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Stats del tipo seleccionado */}
      <div style={{ padding: '8px 20px 4px', fontSize: 13, color: '#5e666e', fontWeight: 500 }}>
        {tipoObj.emoji} {tipoStats.completados}/{tipoStats.total} completados ({tipoStats.pct}%)
        {tipoStats.incidentes > 0 && (
          <span style={{ color: '#ff1744', fontWeight: 700, marginLeft: 8 }}>
            ⚠️ {tipoStats.incidentes} incidente{tipoStats.incidentes > 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Grid de zonas */}
      <div style={{ padding: '12px 16px 100px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        {ZONAS.map(zona => {
          const st = statsZona(tipoActivo, zona.id);
          const borderColor = st.pct >= 70 ? '#22c55e' : st.pct >= 30 ? '#f97316' : '#ef4444';
          const tieneIncidente = st.incidentes > 0;

          return (
            <div
              key={zona.id}
              className={tieneIncidente ? 'zona-incidente' : ''}
              onClick={() => { setZonaActiva(zona.id); setNivel(2); }}
              style={{
                background: '#fff', borderRadius: 14, padding: '16px 14px', cursor: 'pointer',
                border: tieneIncidente ? '2px solid #ff1744' : `2px solid ${borderColor}33`,
                borderLeft: tieneIncidente ? '4px solid #ff1744' : `4px solid ${borderColor}`,
                position: 'relative', transition: 'transform 0.15s',
              }}
            >
              {tieneIncidente && (
                <span style={{
                  position: 'absolute', top: 6, right: 8, fontSize: 16,
                }}>⚠️</span>
              )}
              <div style={{ fontSize: 24, marginBottom: 6 }}>{zona.emoji}</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#333', marginBottom: 8, lineHeight: 1.2 }}>
                {zona.nombre}
              </div>
              <div style={{ background: '#f0f0f0', borderRadius: 6, height: 6, overflow: 'hidden', marginBottom: 6 }}>
                <div style={{
                  width: `${st.pct}%`, height: '100%', borderRadius: 6, transition: 'width 0.5s',
                  background: tieneIncidente ? '#ff1744' : borderColor,
                }} />
              </div>
              <div style={{ fontSize: 11, color: '#5e666e', fontWeight: 500 }}>
                {st.completados}/{st.total} tareas
              </div>
            </div>
          );
        })}
      </div>

      {toast && <Toast mensaje={toast} />}
    </div>
  );
}

// ============================================
// COMPONENTE: DETALLE DE PUNTO (Bottom Sheet)
// ============================================
function DetallesPunto({ punto, tipo, zona, usuario, onGuardar, onCerrar }) {
  const [estado, setEstado] = useState(punto.estado);
  const [tecnico, setTecnico] = useState(punto.tecnico);
  const [notas, setNotas] = useState(punto.notas);
  const [foto, setFoto] = useState(punto.foto);
  const [incidenteDesc, setIncidenteDesc] = useState(punto.incidente_desc || '');
  const [urgencia, setUrgencia] = useState(punto.incidente_urgencia || 'media');
  const sheetRef = useRef(null);

  const numPunto = parseInt(punto.id.split('-')[2]);

  const handleFoto = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setFoto(ev.target.result);
    reader.readAsDataURL(file);
  };

  const puedeGuardar = () => {
    if (estado === 'completado' && !foto) return false;
    if (estado === 'incidente' && !incidenteDesc.trim()) return false;
    return true;
  };

  const guardar = () => {
    if (!puedeGuardar()) return;
    const hora = estado === 'completado'
      ? new Date().toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })
      : punto.hora;
    onGuardar({
      estado,
      tecnico,
      notas,
      foto,
      hora,
      incidente_desc: estado === 'incidente' ? incidenteDesc : '',
      incidente_urgencia: estado === 'incidente' ? urgencia : 'media',
    });
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 100,
      fontFamily: PP,
    }}>
      {/* Overlay */}
      <div onClick={onCerrar} style={{
        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.5)',
      }} />

      {/* Sheet */}
      <div ref={sheetRef} style={{
        position: 'absolute', bottom: 0, left: 0, right: 0,
        background: '#fff', borderRadius: '20px 20px 0 0',
        maxHeight: '85dvh', overflowY: 'auto',
        boxShadow: '0 -4px 20px rgba(0,0,0,0.15)',
      }}>
        {/* Handle */}
        <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0 4px' }}>
          <div style={{ width: 40, height: 4, borderRadius: 2, background: '#d0d0d0' }} />
        </div>

        {/* Header */}
        <div style={{ padding: '8px 20px 16px', borderBottom: '1px solid #f0f0f0' }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: '#333' }}>
            Punto {numPunto}
          </div>
          <div style={{ fontSize: 13, color: '#5e666e', marginTop: 2 }}>
            {zona.emoji} {zona.nombre} — {tipo.emoji} {tipo.label}
          </div>
          {/* Estado actual badge */}
          <div style={{
            display: 'inline-block', marginTop: 8,
            background: ESTADOS[estado].color, color: ESTADOS[estado].texto,
            padding: '6px 14px', borderRadius: 20, fontSize: 13, fontWeight: 700,
          }}>
            {ESTADOS[estado].label}
          </div>
        </div>

        <div style={{ padding: '16px 20px 32px' }}>
          {/* Botones de estado 2x2 */}
          <div style={{ fontSize: 13, fontWeight: 600, color: '#333', marginBottom: 10 }}>Estado</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 20 }}>
            {[
              { key: 'pendiente', icon: '⏳', label: 'Pendiente' },
              { key: 'en_proceso', icon: '🔄', label: 'En proceso' },
              { key: 'completado', icon: '✅', label: 'Completado' },
              { key: 'incidente', icon: '🚨', label: 'Incidente' },
            ].map(({ key, icon, label }) => {
              const est = ESTADOS[key];
              const activo = estado === key;
              const esIncidente = key === 'incidente';
              return (
                <button
                  key={key}
                  onClick={() => setEstado(key)}
                  style={{
                    height: 52, borderRadius: 12, border: `2px solid ${activo ? est.texto : '#e0e0e0'}`,
                    background: activo ? est.color : '#fafafa',
                    color: activo ? est.texto : '#666',
                    fontWeight: 700, fontSize: 14, cursor: 'pointer', fontFamily: PP,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    ...(esIncidente && activo ? { background: '#ff1744', color: '#fff', border: '2px solid #ff1744' } : {}),
                  }}
                >
                  {icon} {label}
                </button>
              );
            })}
          </div>

          {/* Campo tecnico */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#333', marginBottom: 6 }}>Técnico</div>
            <input
              value={tecnico}
              onChange={(e) => setTecnico(e.target.value)}
              placeholder="Técnico que lo instaló"
              style={{
                width: '100%', padding: '12px 14px', borderRadius: 12, border: '1px solid #ddd',
                fontSize: 14, fontFamily: PP, boxSizing: 'border-box', outline: 'none',
              }}
            />
          </div>

          {/* Notas */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#333', marginBottom: 6 }}>Notas</div>
            <textarea
              value={notas}
              onChange={(e) => setNotas(e.target.value)}
              placeholder="Notas adicionales..."
              rows={3}
              style={{
                width: '100%', padding: '12px 14px', borderRadius: 12, border: '1px solid #ddd',
                fontSize: 14, fontFamily: PP, resize: 'vertical', boxSizing: 'border-box', outline: 'none',
              }}
            />
          </div>

          {/* Foto de evidencia */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#333', marginBottom: 6 }}>
              Foto de evidencia
              {estado === 'completado' && <span style={{ color: '#ef4444', marginLeft: 4 }}>*obligatoria</span>}
              {estado === 'incidente' && <span style={{ color: '#f97316', marginLeft: 4 }}>(recomendada)</span>}
            </div>
            {foto ? (
              <div style={{ position: 'relative' }}>
                <img src={foto} alt="Evidencia" style={{
                  width: '100%', borderRadius: 12, maxHeight: 200, objectFit: 'cover',
                }} />
                <label style={{
                  position: 'absolute', bottom: 8, right: 8,
                  background: 'rgba(0,0,0,0.6)', color: '#fff', borderRadius: 8,
                  padding: '6px 12px', fontSize: 12, cursor: 'pointer',
                }}>
                  Cambiar
                  <input type="file" accept="image/*" capture="environment" onChange={handleFoto} style={{ display: 'none' }} />
                </label>
              </div>
            ) : (
              <label style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                background: '#fafafa', border: '2px dashed #ccc', borderRadius: 12, padding: '24px 16px',
                cursor: 'pointer', color: '#888', minHeight: 44,
              }}>
                <span style={{ fontSize: 28, marginBottom: 6 }}>{'📷'}</span>
                <span style={{ fontSize: 13, fontWeight: 600 }}>Tomar foto / Subir imagen</span>
                <input type="file" accept="image/*" capture="environment" onChange={handleFoto} style={{ display: 'none' }} />
              </label>
            )}
          </div>

          {/* Campos de incidente */}
          {estado === 'incidente' && (
            <div style={{
              background: '#fff5f5', borderRadius: 14, padding: 16, marginBottom: 16,
              border: '1px solid #ffcdd2',
            }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#c62828', marginBottom: 12 }}>
                {'🚨'} Reporte de incidente
              </div>
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#333', marginBottom: 6 }}>
                  Describe el problema <span style={{ color: '#ef4444' }}>*</span>
                </div>
                <textarea
                  value={incidenteDesc}
                  onChange={(e) => setIncidenteDesc(e.target.value)}
                  placeholder="Descripción del problema..."
                  rows={3}
                  style={{
                    width: '100%', padding: '12px 14px', borderRadius: 12, border: '1px solid #ffcdd2',
                    fontSize: 14, fontFamily: PP, resize: 'vertical', boxSizing: 'border-box', outline: 'none',
                    background: '#fff',
                  }}
                />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#333', marginBottom: 6 }}>Urgencia</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {['alta', 'media', 'baja'].map(u => (
                    <button
                      key={u}
                      onClick={() => setUrgencia(u)}
                      style={{
                        flex: 1, padding: '10px 8px', borderRadius: 10,
                        border: urgencia === u ? '2px solid #c62828' : '2px solid #e0e0e0',
                        background: urgencia === u ? '#ffebee' : '#fff',
                        color: urgencia === u ? '#c62828' : '#666',
                        fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: PP,
                        textTransform: 'capitalize', minHeight: 44,
                      }}
                    >
                      {u}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Validacion mensajes */}
          {estado === 'completado' && !foto && (
            <div style={{ fontSize: 12, color: '#ef4444', marginBottom: 8, fontWeight: 500 }}>
              ⚠️ Se requiere foto para marcar como completado
            </div>
          )}
          {estado === 'incidente' && !incidenteDesc.trim() && (
            <div style={{ fontSize: 12, color: '#ef4444', marginBottom: 8, fontWeight: 500 }}>
              ⚠️ Describe el problema para reportar incidente
            </div>
          )}

          {/* Boton guardar */}
          <button
            onClick={guardar}
            disabled={!puedeGuardar()}
            style={{
              width: '100%', padding: 16, borderRadius: 14, border: 'none',
              background: !puedeGuardar() ? '#ccc'
                : estado === 'incidente' ? 'linear-gradient(135deg, #ff1744, #d50000)'
                : 'linear-gradient(135deg, #2f5f9e, #5c9adb)',
              color: '#fff', fontSize: 16, fontWeight: 700,
              cursor: puedeGuardar() ? 'pointer' : 'default',
              fontFamily: PP, opacity: puedeGuardar() ? 1 : 0.6,
            }}
          >
            {estado === 'incidente' ? '🚨 Reportar incidente' : '💾 Guardar cambios'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// TOAST
// ============================================
function Toast({ mensaje }) {
  return (
    <div style={{
      position: 'fixed', bottom: 30, left: '50%', transform: 'translateX(-50%)',
      background: '#166534', color: '#fff', padding: '12px 24px', borderRadius: 12,
      fontSize: 14, fontWeight: 600, boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
      zIndex: 200, fontFamily: PP, animation: 'fadeIn 0.3s',
    }}>
      ✅ {mensaje}
    </div>
  );
}
